import { trackEvent } from "@/lib/audit";

/**
 * PR-9 Fase 5 + PR-14A: strict allowlist for AUTOMATIC Halftime Hub
 * openings. Manual opens (FAB / header button) are always allowed and must
 * NOT call this gate. Any auto-open caller must route through
 * `allowAutoOpen()`. PR-14A further enforces a hard lockout window after
 * any user interaction (polls, surveys, predictions, rewards, chat, etc.).
 */
export type AutoOpenTrigger =
  | "halftime"
  | "commercial_break"
  | "prolonged_stoppage"
  | "no_match_mode"
  | "post_match";

const ALLOWED: ReadonlySet<AutoOpenTrigger> = new Set<AutoOpenTrigger>([
  "halftime",
  "commercial_break",
  "prolonged_stoppage",
  "no_match_mode",
  "post_match",
]);

/** Reasons that must NEVER auto-open the hub (PR-14A absolute block). */
const FORBIDDEN_REASONS = new Set<string>([
  "poll_completed",
  "survey_completed",
  "survey_submitted",
  "sponsored_survey_submitted",
  "sponsored_poll_answered",
  "prediction_answered",
  "prediction_completed",
  "reward_claimed",
  "voucher_redeemed",
  "reward_voucher_redeemed",
  "chat_message_sent",
  "community_message_sent",
  "ai_query_sent",
  "ad_clicked",
  "sponsor_interaction",
  "any_user_interaction",
]);

/**
 * PR-14A: events that count as a "user interaction" and trigger the
 * lockout window below. Any of these, when fired through `trackEvent`,
 * will suppress the next auto-open attempt for `INTERACTION_LOCKOUT_MS`.
 */
export const USER_INTERACTION_EVENTS: ReadonlySet<string> = new Set<string>([
  "poll_completed",
  "poll_answered",
  "survey_completed",
  "survey_submitted",
  "sponsored_survey_submitted",
  "sponsored_poll_answered",
  "prediction_answered",
  "prediction_completed",
  "reward_claimed",
  "voucher_redeemed",
  "reward_voucher_redeemed",
  "chat_message_sent",
  "community_message_sent",
  "ai_query_sent",
  "ad_clicked",
  "sponsor_interaction",
]);

const INTERACTION_LOCKOUT_MS = 90_000;
let lastInteractionAt = 0;
let lastInteractionReason: string | null = null;

/** Called by the audit pipeline when a user-interaction event fires. */
export function noteUserInteraction(reason: string) {
  if (!USER_INTERACTION_EVENTS.has(reason)) return;
  lastInteractionAt = Date.now();
  lastInteractionReason = reason;
}

/** Live phases during which auto-open is suppressed even if reason is allowed. */
export type LivePhase =
  | "live"
  | "goal"
  | "attack"
  | "penalty"
  | "dangerous_play"
  | "neutral";

const SUPPRESS_PHASES = new Set<LivePhase>(["goal", "attack", "penalty", "dangerous_play"]);

export function allowAutoOpen(
  reason: string,
  phase: LivePhase = "neutral",
  ctx: { minute?: number } = {},
): reason is AutoOpenTrigger {
  const timestamp = new Date().toISOString();
  const basePayload = {
    attempted_reason: reason,
    current_match_state: phase,
    minute: ctx.minute ?? null,
    timestamp,
  };
  if (FORBIDDEN_REASONS.has(reason) || !ALLOWED.has(reason as AutoOpenTrigger)) {
    void trackEvent("halftime_auto_open_blocked", { ...basePayload, cause: "forbidden_reason" });
    return false;
  }
  if (SUPPRESS_PHASES.has(phase)) {
    void trackEvent("halftime_auto_open_blocked", { ...basePayload, cause: "live_action" });
    return false;
  }
  const sinceInteraction = Date.now() - lastInteractionAt;
  if (lastInteractionAt > 0 && sinceInteraction < INTERACTION_LOCKOUT_MS) {
    void trackEvent("halftime_auto_open_blocked", {
      ...basePayload,
      cause: "recent_user_interaction",
      last_interaction_reason: lastInteractionReason,
      ms_since_interaction: sinceInteraction,
    });
    return false;
  }
  void trackEvent("halftime_auto_open_allowed", basePayload);
  return true;
}